import { useRef, useState } from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { useQueryClient } from '@tanstack/react-query'

export function SettingsPage() {
  const { restaurant, setRestaurant } = useAuthStore()
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState({
    name: restaurant?.name ?? '',
    legal_name: restaurant?.legal_name ?? '',
    address: restaurant?.address ?? '',
    city: restaurant?.city ?? '',
    cuisine_type: restaurant?.cuisine_type ?? '',
    billing_email: restaurant?.billing_email ?? '',
    measurement_system: (restaurant?.measurement_system ?? 'metric') as 'metric' | 'imperial',
    order_code_prefix: restaurant?.order_code_prefix ?? '',
    slug: restaurant?.slug ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  const storefrontUrl = restaurant?.slug ? `${window.location.origin}/store/${restaurant.slug}` : null
  function handleCopyStorefrontLink() {
    if (!storefrontUrl) return
    navigator.clipboard.writeText(storefrontUrl)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  async function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !restaurant) return
    setLogoUploading(true)
    setLogoError(null)
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
      const path = `${restaurant.id}/logo.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('logos')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (uploadErr) throw uploadErr

      const { data: urlData } = supabase.storage.from('logos').getPublicUrl(path)
      const logoUrl = `${urlData.publicUrl}?t=${Date.now()}`

      const { data, error: updateErr } = await supabase
        .from('restaurants')
        .update({ logo_url: logoUrl })
        .eq('id', restaurant.id)
        .select()
        .single()
      if (updateErr) throw updateErr
      setRestaurant(data as typeof restaurant)
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setLogoUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleSave() {
    if (!restaurant) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .update({
          name: form.name,
          legal_name: form.legal_name || null,
          address: form.address || null,
          city: form.city || null,
          cuisine_type: form.cuisine_type || null,
          billing_email: form.billing_email || null,
          measurement_system: form.measurement_system,
          order_code_prefix: form.order_code_prefix.trim().toUpperCase() || null,
          slug: form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') || null,
        })
        .eq('id', restaurant.id)
        .select()
        .single()
      if (error) throw error
      setRestaurant(data as typeof restaurant)
      setSaved(true)
      qc.invalidateQueries()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl space-y-8">
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Settings</h1>
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Brand Logo</h2>
        <p className="text-xs text-stone-400">Used on printed invoices. Max 2 MB — JPG, PNG, or WebP.</p>
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-lg border border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden shrink-0">
            {restaurant?.logo_url ? (
              <img src={restaurant.logo_url} alt="Logo" className="h-full w-full object-contain p-1" />
            ) : (
              <span className="text-2xl font-bold text-stone-300">{restaurant?.name?.[0] ?? 'R'}</span>
            )}
          </div>
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleLogoChange}
            />
            <Button
              variant="secondary"
              size="sm"
              loading={logoUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {restaurant?.logo_url ? 'Change Logo' : 'Upload Logo'}
            </Button>
            {logoError && <p className="text-xs text-danger">{logoError}</p>}
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Restaurant</h2>

        {error && <p className="text-sm text-danger">{error}</p>}
        {saved && <p className="text-sm text-success">Saved successfully.</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Restaurant Name"
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="Legal Name"
            value={form.legal_name}
            onChange={e => setForm(f => ({ ...f, legal_name: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <Input
              label="Org Short Code"
              value={form.order_code_prefix}
              maxLength={3}
              placeholder="e.g. AA"
              onChange={e => setForm(f => ({ ...f, order_code_prefix: e.target.value.toUpperCase().slice(0, 3) }))}
            />
            <p className="text-xs text-stone-400">
              Up to 3 letters — prefixes order numbers, e.g. "{(form.order_code_prefix || 'CAT')}-260821-01".
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="City"
            value={form.city}
            onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
          />
          <Input
            label="Cuisine Type"
            value={form.cuisine_type}
            onChange={e => setForm(f => ({ ...f, cuisine_type: e.target.value }))}
          />
        </div>

        <Input
          label="Address"
          value={form.address}
          onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
        />

        <Input
          label="Billing Email"
          type="email"
          value={form.billing_email}
          onChange={e => setForm(f => ({ ...f, billing_email: e.target.value }))}
        />
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Kitchen Preferences</h2>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-stone-700">Measurement System</label>
          <p className="text-xs text-stone-400">Used when AI extracts ingredients from pasted recipe notes.</p>
          <div className="flex gap-2 mt-1">
            {(['metric', 'imperial'] as const).map(sys => (
              <button
                key={sys}
                type="button"
                onClick={() => setForm(f => ({ ...f, measurement_system: sys }))}
                className={[
                  'px-4 py-2 rounded border text-sm font-medium transition-colors',
                  form.measurement_system === sys
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-stone-700 border-stone-300 hover:border-brand-400',
                ].join(' ')}
              >
                {sys === 'metric' ? 'Metric (g, kg, ml, L)' : 'Imperial (oz, lbs, cups)'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">Storefront</h2>
        <p className="text-xs text-stone-400">
          A public web store where customers can browse products marked "In-Store" and order directly —
          no login required for them. Works today at this link; will move to your own domain once one's connected.
        </p>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-stone-700">Store URL slug</label>
          <div className="flex items-center border border-stone-300 rounded overflow-hidden max-w-md">
            <span className="px-2 py-2 text-xs text-stone-400 bg-stone-50 border-r border-stone-300 select-none whitespace-nowrap">
              {window.location.origin}/store/
            </span>
            <input
              type="text"
              value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              placeholder="your-restaurant"
              className="w-full px-2 py-2 text-sm focus:outline-none"
            />
          </div>
        </div>

        {storefrontUrl && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={handleCopyStorefrontLink}>
              {linkCopied ? <Check size={13} /> : <Copy size={13} />}
              {linkCopied ? 'Copied' : 'Copy Link'}
            </Button>
            <a
              href={storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-300 text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors"
            >
              <ExternalLink size={13} />
              View Store
            </a>
          </div>
        )}
      </div>

      <Button onClick={handleSave} loading={saving}>
        Save Settings
      </Button>

      <p className="text-xs text-stone-300 pt-4 text-center select-none">
        Powered by Rezzops
      </p>
    </div>
  )
}
